import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Divider,
  Flex,
  Heading,
  HStack,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from '@chakra-ui/react';
import { CargoType, DocumentType, FreightService } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { ConditionalField, DOCK_HOURS_OPTIONS, freightServiceAddOnOptions } from '@pelicargo/utils';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { HiOutlineExclamation } from 'react-icons/hi';

import { API } from '../../utils/api-typings';
import { SimpleDatePickerInput } from '../DatePicker/SimpleDatePickerInput';
import { DocumentsForm } from '../Documents/DocumentsForm ';
import { SimpleClosestAirportPicker } from '../RoutePicker/SimpleClosestAirportPicker';
import { SimpleCheckboxGroup } from '../SimpleCheckboxGroup';
import { SimpleDropzone } from '../SimpleDropzone';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleTextarea } from '../SimpleTextarea';
import { ConditionalFields } from './ConditionalFieldsForm';

type Props = {
  quote: API.GetQuote;
  conditionalFields: ConditionalField[];
};

export const InlandBookingForm = ({ quote, conditionalFields }: Props) => {
  const methods = useFormContext();

  const requestId = quote?.request_id;
  const request = quote?.request;
  const expiresAt = quote?.expires_at;

  const documentsInForm = methods.watch('documents');

  const isDangerousGoods = useMemo(() => request?.cargo_type === CargoType.DangerousGoods, [request?.cargo_type]);
  const shouldShowDgdWarning = useMemo(() => {
    const hasDocument = documentsInForm?.length > 0;

    if (isDangerousGoods && !hasDocument) return true;
    return false;
  }, [documentsInForm, isDangerousGoods]);

  const isExpired = useMemo(() => {
    if (!expiresAt) return false;

    const convertToTZ = (date: Date, timeZone: string) => {
      const newDate = new Date(date.toLocaleString('en-US', { timeZone }));
      return newDate;
    };
    const originTimeZone = quote?.origin_airport?.time_zone;
    const adjustedExpiresAt = convertToTZ(expiresAt, originTimeZone);
    adjustedExpiresAt.setHours(23, 59, 59, 999);
    const now = convertToTZ(new Date(), originTimeZone);

    return adjustedExpiresAt < now;
  }, [expiresAt, quote?.origin_airport?.time_zone]);

  const isFCA = useMemo(
    () => quote?.request?.freight_services?.includes(FreightService.FCA),
    [quote?.request?.freight_services],
  );

  const isDgd = useMemo(() => request?.cargo_type === CargoType.DangerousGoods, [request?.cargo_type]);

  const documentTypes = useMemo(() => {
    const suggested: DocumentType[] = [
      DocumentType.SLI,
      DocumentType.SHIPPER_DOCUMENTS,
      DocumentType.AIRWAY_BILL,
      DocumentType.BOL,
    ];

    if (!isFCA) suggested.push(DocumentType.LABELS);
    if (isDgd) suggested.push(DocumentType.DGD, DocumentType.MSDS);

    return suggested;
  }, [isFCA, isDgd]);

  return (
    <Stack w="full" spacing="3">
      <Stack spacing="5" divider={<Divider />}>
        {/* Using this instead of <Show/> so <Divider/> doesn't render */}
        {!isFCA && (
          <Stack spacing="8">
            <Stack>
              <Heading variant="h5">Pickup</Heading>
              <Heading fontSize="sm" textTransform="uppercase">
                Details
              </Heading>
              <Stack>
                <Stack direction={{ base: 'column', sm: 'row' }} w="full">
                  <Stack direction="row" w="full">
                    <SimpleDatePickerInput
                      name="pickup_ready_at"
                      label="Ready Date *"
                      variant="outline"
                      iconFontSize="20px"
                      placeholder="Ready Date"
                    />
                  </Stack>
                  <Stack direction="row" align="center" w="full" spacing="1">
                    <SimpleSelect
                      name="from_dock_hours"
                      label="Dock Hours *"
                      options={DOCK_HOURS_OPTIONS}
                      variant="outline"
                    />
                    <Text>-</Text>
                    <SimpleSelect
                      name="to_dock_hours"
                      label="Dock Hours *"
                      options={DOCK_HOURS_OPTIONS}
                      variant="outline"
                    />
                  </Stack>
                </Stack>
                <SimpleClosestAirportPicker />
                <SimpleInput
                  name="pickup_ref"
                  label="Pickup Reference #"
                  placeholder="x x x   x x x   x x x"
                  variant="outline"
                />
              </Stack>
            </Stack>
            <Stack>
              <Heading fontSize="sm" textTransform="uppercase">
                Contact (optional)
              </Heading>
              <Stack direction="row" w="full">
                <SimpleInput
                  name="inland_dropoff_contact.full_name"
                  label="Full Name"
                  placeholder="Full Name"
                  flex="1"
                />
                <SimpleInput name="inland_dropoff_contact.phone_number" label="Phone" placeholder="Phone" flex="1" />
                <SimpleInput name="inland_dropoff_contact.email" label="Email" placeholder="Email" flex="1" />
              </Stack>
            </Stack>
          </Stack>
        )}
        {isFCA && (
          <Stack>
            <Heading variant="h5">Drop-off</Heading>
            <SimpleDatePickerInput
              name="dropoff_at"
              label="Drop-off date *"
              variant="outline"
              iconFontSize="20px"
              placeholder="Drop-off date"
            />
            <SimpleInput
              name="trucking_name"
              label="Trucking Company (Optional)"
              placeholder="Trucking Company"
              flex="1"
              variant="outline"
            />
          </Stack>
        )}
        <Stack>
          <Heading variant="h5">Paperwork</Heading>
          <HStack>
            <SimpleCheckboxGroup
              name="freight_service_add_ons"
              options={freightServiceAddOnOptions}
              direction="row"
              spacing="4"
              checkboxProps={{ size: 'md' }}
            />
          </HStack>

          <SimpleTextarea
            name="awb_instructions"
            label="AWB Instructions"
            placeholder="AWB Instructions"
            variant="outline"
            minH="150px"
            wordBreak="keep-all"
          />
          <Heading variant="h5">Documents</Heading>
          <DocumentsForm name="documents" requestId={requestId} documentTypes={documentTypes} />
        </Stack>
        {conditionalFields?.length && <ConditionalFields quote={quote} fields={conditionalFields} />}
        <Stack spacing="6">
          <Stack>
            <Heading variant="h5">Additional Information (Optional)</Heading>
            <SimpleInput
              label="Your Reference #"
              name="customer_ref"
              placeholder="x x x   x x x   x x x"
              variant="outline"
              flex="1"
            />
          </Stack>
          <SimpleTextarea name="additional_information" label="Notes" placeholder="Notes" variant="outline" rows={2} />
        </Stack>
      </Stack>

      <Show if={shouldShowDgdWarning}>
        <Flex>
          <Tag size="lg" borderRadius="full" variant="outline" colorScheme="red" py="2">
            <TagLeftIcon as={HiOutlineExclamation} boxSize="6" />
            <TagLabel fontSize="lg">Missing Dangerous Goods Declaration - Upload to Book</TagLabel>
          </Tag>
        </Flex>
      </Show>
      <Show if={isDangerousGoods}>
        <SimpleDropzone name="documents" documentType={DocumentType.DGD} requestId={requestId} />
      </Show>
      <Show if={isExpired}>
        <Flex>
          <Alert status="error" variant="subtle" fontSize="sm" borderRadius="md">
            <AlertIcon />
            <Stack spacing="1">
              <AlertTitle>Quote Expired</AlertTitle>
              <AlertDescription>
                This quote has passed its expiry date. Booking this may not result in the same price.
              </AlertDescription>
            </Stack>
          </Alert>
        </Flex>
      </Show>
    </Stack>
  );
};
